import React, { useState, useEffect } from 'react'
import useQuickSchedule from './hooks/useQuickSchedule'
import LoadingView from './components/LoadingView'
import ErrorView from './components/ErrorView'
import AppointmentPanel from './components/AppointmentPanel'
import SchedulerPanel from './components/SchedulerPanel'
import { checkConsolidation } from './utils/appointmentAPI'
import CarrierNavBar from '../Carrier/Components/CarrierNavBar'
import PoweredByFooter from '../Footer/PoweredByFooter'
import { ParentShipmentProvider } from './context/ParentShipmentContext'
import isShipmentExpired from '../../utils/helpers/isShipmentExpired'

// ─── Inner component: one shipment's scheduling UI ─────────────────────────

const ShipmentScheduler = ({ driverCode, isConsolidation, stopType, enhancedDriverCodeOBJ }) => {
    const {
        status, errorMessage, data,
        adminDate, setAdminDate,
        pickupOrDelivery, setPickupOrDelivery,
        reload
    } = useQuickSchedule(driverCode, stopType)

    if (status === 'loading') return <LoadingView />
    if (status !== 'ready') return <ErrorView status={status} message={errorMessage} />

    // For consolidation stops: don't render until pickupOrDelivery is resolved
    if (isConsolidation && !pickupOrDelivery) return null

    const showPickupInfo =
        data.shipment.client.id !== 'kwiktrip' &&
        !(isConsolidation && enhancedDriverCodeOBJ?.stopType === 'Delivery')

    const showDeliveryInfo =
        !(isConsolidation && enhancedDriverCodeOBJ?.stopType === 'Pickup')

    return (
        <div className="container-fluid">
            <div className="row g-0">
                {/* Left column: shipment info, status badges, addresses */}
                <div className="col-12 col-md-4 border-end">
                    <AppointmentPanel
                        shipment={data.shipment}
                        isConsolidation={isConsolidation}
                        existingPickupAppts={data.existingPickupAppts}
                        existingDeliveryAppts={data.existingDeliveryAppts}
                        pickupRequired={data.pickupRequired}
                        deliveryRequired={data.deliveryRequired}
                        pickupOrDelivery={pickupOrDelivery}
                        setPickupOrDelivery={setPickupOrDelivery}
                        shipperLocationInfo={data.shipperLocationInfo}
                        consigneeLocationInfo={data.consigneeLocationInfo}
                        adminDate={adminDate}
                        setAdminDate={setAdminDate}
                        reload={reload}
                        schedulingPickup={false}
                        schedulingDelivery={false}
                        showPickupInfo={showPickupInfo}
                        showDeliveryInfo={showDeliveryInfo}
                        stopType={stopType}
                    />
                </div>

                {/* Right column: open time window selector */}
                <div className="col-12 col-md-8">
                    <SchedulerPanel
                        pickupOrDelivery={pickupOrDelivery}
                        consolidatedOpenTimeWindows={data.consolidatedOpenTimeWindows}
                        existingPickupAppts={data.existingPickupAppts}
                        existingDeliveryAppts={data.existingDeliveryAppts}
                        shipment={data.shipment}
                        reload={reload}
                    />
                </div>
            </div>
        </div>
    )
}

// ─── Outer component: handles consolidation check, nav, footer ──────────────

const QuickSchedule = (props) => {
    const driverCode = props.match.params.driverCode
    const searchParams = new URLSearchParams(props.location.search)
    const showHeader = searchParams.get('showHeader') !== 'false'
    const showFooter = searchParams.get('showFooter') !== 'false'

    const [consolidationStatus, setConsolidationStatus] = useState('loading')
    const [isConsolidation, setIsConsolidation] = useState(false)
    const [stops, setStops] = useState([])
    const [client, setClient] = useState(null)
    const [parentShipment, setParentShipment] = useState(null)
    const [bolCopied, setBolCopied] = useState(false)

    const copyBol = (id) => {
        navigator.clipboard.writeText(String(id)).then(() => {
            setBolCopied(true)
            setTimeout(() => setBolCopied(false), 2000)
        })
    }

    useEffect(() => {
        if (!driverCode) {
            setConsolidationStatus('error')
            return
        }
        checkConsolidation(driverCode).then(result => {
            if (!result) {
                setConsolidationStatus('error')
                return
            }
            setClient(result.client)
            if (result.shipV4) setParentShipment(result.shipV4)
            if (result.isConsolidation) {
                setIsConsolidation(true)
                // Prefer the enhanced driver code objects (have stopType) over the basic string array
                if (result.stopsDriverCodesEnhanced?.length) {
                    setStops(result.stopsDriverCodesEnhanced)
                } else if (result.stopsDriverCodes?.length) {
                    setStops(result.stopsDriverCodes.map((dc, i) => ({
                        driverCode: dc,
                        stopNumber: i,
                        stopType: null
                    })))
                }
                setConsolidationStatus('consolidation')
            } else {
                setConsolidationStatus('ready')
            }
        })
    }, [driverCode]) // eslint-disable-line react-hooks/exhaustive-deps

    if (consolidationStatus === 'loading') return <LoadingView />

    if (consolidationStatus === 'error') {
        return <ErrorView status="error" message="Failed to load shipment. Please try reloading." />
    }

    return (
        <ParentShipmentProvider driverCode={driverCode}>
        <div>
            {showHeader && <CarrierNavBar client={client} />}



            {/* Option 2: thin colored bar */}
            <div className="bg-primary bg-opacity-10 border border-primary py-1 text-center mb-2">
                <small className="text-primary fw-semibold" style={{ letterSpacing: '0.05em' }}>QUICK SCHEDULE</small>
            </div>
            <hr/>



            {isConsolidation && (
                <div className="d-flex align-items-center justify-content-between my-3 px-3">
                    {/* Left */}
                    <div>
                        {parentShipment && (
                            <div className="border text-start px-3 py-2" style={{ backgroundColor: '#F8F8F8', width: 'fit-content' }}>
                                <div className="mb-2">
                                    <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>BOL #</div>
                                    <div className="fw-semibold" onClick={() => copyBol(parentShipment.id)} style={{ cursor: 'pointer' }}>
                                        {parentShipment.id}
                                        {bolCopied && <span className="ms-2 badge bg-secondary fw-normal" style={{ fontSize: '0.7rem' }}>Copied!</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>Carrier</div>
                                    <div className="fw-semibold" title={parentShipment.carrier?.carrierid || ''}>
                                        {parentShipment.carrier?.name || 'No carrier assigned'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Center */}
                    <div style={{ flex: '1' }} className="d-flex justify-content-center">
                        {stops.length > 1 && (
                            <div className="alert alert-success border-dark mb-0" role="alert">
                                There are {stops.length} stops that require an appointment.{' '}
                                Scroll down to see all stops.
                            </div>
                        )}
                    </div>

                    {/* Right — expiry check, only shown when expired */}
                    <div>
                        {parentShipment && (() => {
                            const { expired, reason } = isShipmentExpired(parentShipment)
                            if (!expired) return null
                            return (
                                <div className="border text-start px-3 py-2" style={{ backgroundColor: '#F8F8F8', width: 'fit-content' }}>
                                    <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>Carrier Scheduling Page</div>
                                    <div className="fw-semibold text-danger">Not Active</div>
                                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{reason}</div>
                                    <a
                                        href={`/carrier/${driverCode}/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="d-block mt-1"
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        View scheduling page ↗
                                    </a>
                                </div>
                            )
                        })()}
                    </div>
                </div>
            )}

            {!isConsolidation && parentShipment && (() => {
                const { expired, reason } = isShipmentExpired(parentShipment)
                if (!expired) return null
                return (
                    <div className="d-flex justify-content-center my-3 px-3">
                        <div className="border text-start px-3 py-2" style={{ backgroundColor: '#F8F8F8', width: 'fit-content' }}>
                            <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>Carrier Scheduling Page</div>
                            <div className="fw-semibold text-danger">Expired</div>
                            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{reason}</div>
                            <a
                                href={`/carrier/${driverCode}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-block mt-1"
                                style={{ fontSize: '0.75rem' }}
                            >
                                View expired page ↗
                            </a>
                        </div>
                    </div>
                )
            })()}

            {isConsolidation ? (
                stops.map((stop, i) => (
                    <div key={stop.stopNumber ?? i} className="mt-3">
                        <ShipmentScheduler
                            driverCode={stop.driverCode}
                            isConsolidation={true}
                            stopType={stop.stopType}
                            enhancedDriverCodeOBJ={stop}
                        />
                    </div>
                ))
            ) : (
                <ShipmentScheduler
                    driverCode={driverCode}
                    isConsolidation={false}
                    stopType={null}
                    enhancedDriverCodeOBJ={null}
                />
            )}

            {showFooter && <PoweredByFooter />}
        </div>
        </ParentShipmentProvider>
    )
}

export default QuickSchedule
